import React, {useCallback, useEffect, useState} from 'react';
import {Button, Card, CardActions, CardContent, CardMedia, Skeleton, Stack, Typography} from "@mui/material";
import {formatPrice} from 'src/functions/priceFormatter';
import {useNavigate} from "react-router";
import {apiDateTimeStringToDate} from "src/api/apiDateTimeHandler";
import Box from "@mui/material/Box";
import projectLocationTypeEnum from "src/enum/projectLocationTypeEnum";
import myProjectStatusEnum from "src/enum/myProjectStatusEnum";
import projectEligibilityApi from "src/api/project/projectEligibility";
import appointmentApi from "src/api/project/appointment";
import projectService from "src/service/projectService";
import {format} from "date-fns";
import {useConfirm} from "material-ui-confirm";
import Grid2 from "@mui/material/Unstable_Grid2";
import projectApi from 'src/api/project/project';
import CancelAppointmentButton from "src/components/project/CancelAppointmentButton";

const ProjectCard = ({project, projectDetailVersion = false}) => {
  const [projectEligibility, setProjectEligibility] = useState(null);
  const [projectEligibilityLoaded, setProjectEligibilityLoaded] = useState(false);
  const [appointmentLoaded, setAppointmentLoaded] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [cancelError, setCancelError] = useState(false);
  const confirm = useConfirm();

  const fetchProjectEligibility = async (project) => {
    setProjectEligibilityLoaded(false);
    const fetchedProjectEligibilities = await projectEligibilityApi.retrieveCollectionByProject(project.id);
    setProjectEligibility(fetchedProjectEligibilities['hydra:member'] ? fetchedProjectEligibilities['hydra:member'][0] : null);
    setProjectEligibilityLoaded(true);
  }

  const fetchAppointment = async (project) => {
    setAppointmentLoaded(false);
    const fetchedAppointment = await appointmentApi.retrieveCollection({project: project['@id']});
    setAppointment(fetchedAppointment['hydra:member'] ? fetchedAppointment['hydra:member'][0] : null);
    setAppointmentLoaded(true);
  }

  useEffect(() => {
    setProjectEligibilityLoaded(false);
    setAppointmentLoaded(false);
    if (project) {
      fetchProjectEligibility(project);
      fetchAppointment(project);
    }
  }, [project])

  const navigate = useNavigate();
    const goToDetailPage = (project) => {
        navigate(`/projecten/${project.id}`);
    }

    const goToMakeAppointmentPage = (project) => {
      navigate(`/projecten/${project.id}/afspraak-maken`);
    }

    const formatDuration = (minutes) => {
        if (minutes === 1) {
            return '1 minuut'
        }
        if (minutes < 60) {
            return `${minutes} minuten`
        }
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes - (60 * hours);

        return `${hours} uur en ${remainingMinutes} minuten`;
    }

    const formatEnd = (date) => {
      return apiDateTimeStringToDate(date).toLocaleDateString('nl-NL', {
        'day': 'numeric',
        'month': 'long',
        'year': 'numeric',
      });
    }

    const getLocationTypography = () => {
      if (project.locationType === projectLocationTypeEnum.ONLINE) {
        return (
          <>
            <Typography component="span" variant="body1">Online onderzoek</Typography><br />
            <Typography component="span" variant="body1">&nbsp;</Typography>
          </>
        );
      }

      if (project.locationType === projectLocationTypeEnum.AT_HOME) {
        return (
          <>
            <Typography component="span" variant="body1">Thuis testen</Typography><br />
            <Typography component="span" variant="body1">&nbsp;</Typography>
          </>
        );
      }

      const location = project.location;

      const fullHouseNumber = location.houseNumberAddition
        ? `${location.houseNumber}${location.houseNumberAddition}`
        : location.houseNumber;

      return (
        <>
          <Typography component="span" variant="body1">{location.street} {fullHouseNumber}</Typography><br />
          <Typography component="span" variant="body1">{location.postalCode} {location.city}</Typography>
        </>
      );
    }

    const getMyProjectStatus = () => {
      if (!project || !projectEligibilityLoaded || !appointmentLoaded) {
        return null;
      }
      return projectService.getMyProjectStatus(project, appointment, projectEligibility);
    }

    const goToQuestionnaireLink = async () => {
      // Fetch project item, which contains the full link
      const projectItem = await projectApi.retrieveItem(project.id);
      window.location.href = projectItem.questionnaireLink;
    }

  const goToEntryQuestionnaireLink = async () => {
    // Fetch project item, which contains the full link
    const projectItem = await projectApi.retrieveItem(project.id);
    window.location.href = projectItem.entryQuestionnaireLink;
  }

  const notifyMe = useCallback(async (projectEligibility, notifyWhenSpaceBecomesAvailable) => {
    const updatedProjectEligibility = await projectEligibilityApi.update(projectEligibility.id, {
      notifyWhenSpaceBecomesAvailable: notifyWhenSpaceBecomesAvailable
    })
    setProjectEligibility(updatedProjectEligibility);
  }, [setProjectEligibility]);

  const getCardActions = () => {
    const goToDetailPageButton = <Button size="small" variant="outlined" color="secondary" onClick={() => goToDetailPage(project)}>Meer info</Button>;

    switch (getMyProjectStatus()) {
        case myProjectStatusEnum.ONLINE_PROJECT_CAN_FILL_IN_QUESTIONNAIRE:
          return (
            <>
              <Button size="small" variant="outlined" onClick={goToQuestionnaireLink}>Start onderzoek</Button>
              {!projectDetailVersion && goToDetailPageButton}
            </>
          );
        case myProjectStatusEnum.ONLINE_PROJECT_CANNOT_FILL_IN_QUESTIONNAIRE:
        case myProjectStatusEnum.PHYSICAL_PROJECT_ALREADY_PARTICIPATED:
          return <Typography variant="body1">Je hebt al deelgenomen aan dit project.</Typography>;
        case myProjectStatusEnum.PHYSICAL_PROJECT_CAN_FILL_IN_ENTRY_QUESTIONNAIRE:
        case myProjectStatusEnum.PHYSICAL_PROJECT_CAN_FILL_IN_ENTRY_QUESTIONNAIRE_BUT_NO_SPOTS_LEFT:
          return (
            <>
              <Button size="small" variant="outlined" onClick={goToEntryQuestionnaireLink}>Inschrijven</Button>
              {!projectDetailVersion && goToDetailPageButton}
            </>
          );
        case myProjectStatusEnum.PHYSICAL_PROJECT_ENTRY_QUESTIONNAIRE_IS_REJECTED:
          return <Typography variant="body1" color="status.danger.main">Helaas behoort u niet tot onze doelgroep. U kunt dus niet deelnemen.</Typography>;
        case myProjectStatusEnum.PHYSICAL_PROJECT_MAY_MAKE_APPOINTMENT_BUT_NO_SPOTS_LEFT:
          return (
            <Stack direction="row" spacing={2}>
              {
                !projectEligibility.notifyWhenSpaceBecomesAvailable &&
                <Button variant="outlined" size="small" onClick={() => notifyMe(projectEligibility, true)}>Plaats mij op de wachtlijst</Button>
              }
              {
                projectEligibility.notifyWhenSpaceBecomesAvailable &&
                <Button variant="outlined" size="small" color="error" onClick={() => notifyMe(projectEligibility, false)}>Haal mij van de wachtlijst af</Button>
              }
              {!projectDetailVersion && goToDetailPageButton}
            </Stack>
          )
        case myProjectStatusEnum.PHYSICAL_PROJECT_MAY_MAKE_APPOINTMENT:
          return (
            <>
              <Button variant="outlined" size="small" onClick={() => goToMakeAppointmentPage(project)}>Afspraak maken</Button>
              {!projectDetailVersion && goToDetailPageButton}
            </>
          )
        case myProjectStatusEnum.PHYSICAL_PROJECT_HAS_MADE_APPOINTMENT_AND_CAN_CANCEL:
          return (
            <>
              <CancelAppointmentButton
                appointment={appointment}
                setCancelError={setCancelError}
                onSuccess={() => fetchAppointment(project)}
              />
              {!projectDetailVersion && goToDetailPageButton}
              { cancelError && <Typography variant="body1" color="status.primary.main"></Typography> }
            </>
          )
        case myProjectStatusEnum.PHYSICAL_PROJECT_HAS_MADE_APPOINTMENT_AND_CANNOT_CANCEL:
          return null;
        case null:
          return <Skeleton width='100%'><Button /></Skeleton>
        default:
          throw new Error('Unexpected case');
      }
  }

  const getCardContentFinalParagraph = () => {
      switch (getMyProjectStatus()) {
        case myProjectStatusEnum.ONLINE_PROJECT_CAN_FILL_IN_QUESTIONNAIRE:
        case myProjectStatusEnum.ONLINE_PROJECT_CANNOT_FILL_IN_QUESTIONNAIRE:
        case myProjectStatusEnum.PHYSICAL_PROJECT_CAN_FILL_IN_ENTRY_QUESTIONNAIRE:
        case myProjectStatusEnum.PHYSICAL_PROJECT_MAY_MAKE_APPOINTMENT:
        case myProjectStatusEnum.PHYSICAL_PROJECT_MAY_MAKE_APPOINTMENT_BUT_NO_SPOTS_LEFT:
        case myProjectStatusEnum.PHYSICAL_PROJECT_CAN_FILL_IN_ENTRY_QUESTIONNAIRE_BUT_NO_SPOTS_LEFT:
          return (
            <Box mb={2}>
              <Typography component="span" variant="body1" sx={{fontStyle: 'italic'}}>{project.remainingNumberOfAppointments} plekken beschikbaar</Typography><br />
              <Typography component="span" variant="body1">Project is beschikbaar t/m {formatEnd(project.end)}</Typography>
            </Box>
          );
        case myProjectStatusEnum.PHYSICAL_PROJECT_HAS_MADE_APPOINTMENT_AND_CAN_CANCEL:
        case myProjectStatusEnum.PHYSICAL_PROJECT_HAS_MADE_APPOINTMENT_AND_CANNOT_CANCEL:
          if (!appointment) {
            return (
              <Skeleton>
                <Typography component="span" variant="body1">&nsbp;</Typography><br />
                <Typography component="span" variant="body1">&nsbp;</Typography>
              </Skeleton>
            );
          }

          return (
            <Box mb={2}>
              <Typography component="span" variant="body1" sx={{fontStyle: 'italic'}}>Afspraak gemaakt voor</Typography><br />
              <Typography component="span" variant="body1">{format(apiDateTimeStringToDate(appointment.projectInstance.start), 'd MMMM yyyy kk:mm')}</Typography>
            </Box>
          );
        case myProjectStatusEnum.PHYSICAL_PROJECT_ENTRY_QUESTIONNAIRE_IS_REJECTED:
          return (
            <Box mb={2}>
              <Typography component="span" variant="body1">&nbsp;</Typography><br />
              <Typography component="span" variant="body1">&nbsp;</Typography>
            </Box>
          );
      }
  }

  if (!project) {
    return <Skeleton variant="rectangular" />
  }

  if (projectDetailVersion) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Grid2 container>
            <Grid2 xs={12} sm={4}>
              <Box mb={2}>
                <Typography component="span" variant="body1" sx={{fontWeight: 'bold'}}>Locatie:</Typography><br />
                {getLocationTypography()}
              </Box>

              <Box mb={2}>
                <Typography component="span" variant="body1" sx={{fontWeight: 'bold'}}>Wanneer:</Typography><br />
                <Typography component="span" variant="body1">{project.whenDescription}</Typography>
              </Box>

              { project.duration !== null &&
                <Box mb={2}>
                  <Typography component="span" variant="body1" sx={{fontWeight: 'bold'}}>Duur:</Typography><br />
                  <Typography component="span" variant="body1">{formatDuration(project.duration)}</Typography>
                </Box>
              }

              <Box mb={2}>
                <Typography component="span" variant="body1" sx={{fontWeight: 'bold'}}>Vergoeding:</Typography><br />
                <Typography component="span" variant="body1">{formatPrice(project.compensation)}</Typography>
              </Box>
            </Grid2>
            <Grid2 xs={12} sm={8}>
              {getCardContentFinalParagraph()}
              {getCardActions()}
            </Grid2>
          </Grid2>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card variant="outlined">
      {
        (project.image && project.image.contentUrl) ?
        <CardMedia
          sx={{ objectFit: 'contain', height: '200px' }}
          image={project.image.contentUrl}
          alt={project.name}
        />
          : <Skeleton sx={{ objectFit: 'contain', height: '200px' }} />
      }
      <CardContent>
        <Box mb={2}>
          <Typography variant="h5" mb={6}>{project.name}</Typography>
        </Box>

        <Box mb={2}>
          <Typography component="span" variant="body1" sx={{fontWeight: 'bold'}}>Locatie:</Typography><br />
          {getLocationTypography()}
        </Box>

        <Box mb={2}>
          <Typography component="span" variant="body1" sx={{fontWeight: 'bold'}}>Wanneer:</Typography><br />
          <Typography component="span" variant="body1">{project.whenDescription}</Typography>
        </Box>

        { project.duration !== null &&
          <Box mb={2}>
            <Typography component="span" variant="body1" sx={{fontWeight: 'bold'}}>Duur:</Typography><br />
            <Typography component="span" variant="body1">{formatDuration(project.duration)}</Typography>
          </Box>
        }

        <Box mb={2}>
          <Typography component="span" variant="body1" sx={{fontWeight: 'bold'}}>Vergoeding:</Typography><br />
          <Typography component="span" variant="body1">{formatPrice(project.compensation)}</Typography>
        </Box>

        {getCardContentFinalParagraph()}
      </CardContent>
      <CardActions>
          {getCardActions()}
      </CardActions>
    </Card>
  );
}

export default ProjectCard;

ProjectCard.propTypes = {};

ProjectCard.defaultProps = {};
